import { useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useSessionStore } from '@/store/sessionStore';

/**
 * Hook to proactively check session validity when:
 * 1. App becomes visible (user switches back to tab)
 * 2. Window regains focus
 * 3. On initial mount if authenticated
 * 
 * This ensures users are immediately informed when their session has expired,
 * rather than waiting for an API call to fail.
 */
export function useSessionCheck() {
  const { isAuthenticated, clearAuth } = useAuthStore();
  const { setSessionExpired, isSessionExpired } = useSessionStore();
  const lastCheckRef = useRef<number>(0);
  const isCheckingRef = useRef(false);

  // Minimum time between checks (30 seconds) to avoid excessive API calls
  const MIN_CHECK_INTERVAL_MS = 30 * 1000;

  const checkSession = useCallback(async () => {
    // Skip if not authenticated, already checking, or session already expired
    if (!isAuthenticated || isCheckingRef.current || isSessionExpired) {
      return;
    }

    // Skip if we checked recently
    const now = Date.now();
    if (now - lastCheckRef.current < MIN_CHECK_INTERVAL_MS) {
      return;
    }

    // Check if we have a token at all
    const token = localStorage.getItem('accessToken');
    if (!token) {
      // No token but authenticated state - session is invalid
      console.debug('[SessionCheck] No token found, session expired');
      setSessionExpired(true);
      return;
    }

    isCheckingRef.current = true;
    lastCheckRef.current = now;

    try {
      // Make a lightweight API call to check session validity
      // This will trigger token refresh if needed, or fail if session is truly expired
      // Using /me/profile as it's a simple endpoint that requires authentication
      await apiClient.get('/api/v1/me/profile');
      // Session is valid - nothing to do
    } catch (error) {
      // Check if this is an auth failure
      const err = error as { statusCode?: number; message?: string };
      if (err.statusCode === 401) {
        // Session is expired - the API client's handleAuthFailure should have
        // already set isSessionExpired, but we set it here too just in case
        console.debug('[SessionCheck] Session validation failed, showing expiry modal');
        setSessionExpired(true);
        clearAuth();
      }
      // Other errors (network, etc.) are ignored - we don't want to show
      // session expired for temporary network issues
    } finally {
      isCheckingRef.current = false;
    }
  }, [isAuthenticated, isSessionExpired, setSessionExpired, clearAuth]);

  // Check session on visibility change (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSession]);

  // Check session on window focus
  useEffect(() => {
    const handleFocus = () => {
      checkSession();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkSession]);

  // Check session on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Small delay to let the app settle after mount
      const timer = setTimeout(() => {
        checkSession();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, checkSession]);

  return { checkSession };
}
